/**
 * Live config + experiments (our server serves these at GET /api/v1/config).
 *
 * Flags mirror the switches the analysed app reads from its live config (enableStudio,
 * allowSkipCapture, familyPlanPaywallVariant, ...). Experiments assign a deterministic bucket per
 * account and can be targeted with limitTo rules (age range, country, language, device type).
 * Note: gender targeting is intentionally not supported (no gender data is collected).
 */
import { hash } from './mailing';

export interface LiveConfig {
  enableStudio: boolean;
  enableStories: boolean;
  enableWeeklyChallenges: boolean;
  enableTeacherMailing: boolean;
  enableDrawTogether: boolean;
  enableHomeWidgetActivation: boolean;
  skillJourneysEnabled: boolean;
  isSkillProgressionEnabled: boolean;
  requestReviewAppEnabled: boolean;
  enableSuggestions: boolean;
  enableTipsAndTricksAnimatedThumbnails: boolean;
  enableLooseTutorial: boolean;
  allowSkipCapture: boolean;
  skipScanImageForJournal: boolean;
  skipShareBeforeAward: boolean;
  journalMode: 'book' | 'grid';
  familyPlanPaywallVariant: 'familyFirst' | 'cadenceFirst';
  singleFtuePaywall: boolean;
  /** Onboarding steps (DLC-style switches). */
  onboarding: {
    grownUpGate: boolean;
    selfAttributionQuestion: boolean;
    favoriteSubjects: 'selection' | 'animated';
    realDrawer: boolean;
    circleScan: boolean;
    socialProof: boolean;
    reminderNudge: boolean;
    kidLedSecondProfile: boolean;
    ftueUnlockAll: boolean;
  };
  /** Minimum app build; older builds must update (force) or may update (optional). */
  appUpdate: { minimalBuild: number; optionalBuild: number };
  /** Kill-switch for heuristic feedback (fallback = completion animation). */
  recognitionEnabled: boolean;
  /** Show the block screen on managed / supervised (MDM) devices. */
  blockManagedDevices: boolean;
  /** Remote push reminders (server scheduler); local reminders stay as the fallback. */
  enableRemotePush: boolean;
  /** Use the platform review sheet (StoreKit / Play) on native; the web keeps the in-app sheet. */
  nativeReviewEnabled: boolean;
  /**
   * Illustration skin (docs/ART-BIBLE.md): storybook (A, default), sticker (B, e.g. future sticker
   * rewards) or pastel (C, e.g. seasonal / holiday skin). Not user-visible yet (cheats menu only).
   */
  artStyle: ArtStyleName;
  /** WOW · colour: world maps start as a pencil sketch and bloom into colour as lessons are completed. */
  enableColourWorld: boolean;
  /** WOW · colour: stage-5 colouring coach in lessons (palette, light & shadow, colour by zones, tool tips). */
  enableColourCoach: boolean;
  /** WOW · feel: Teacher Juno reaction bubbles (expressive pose + short voice line). */
  junoReactions: boolean;
  /** Pre-rendered natural voices (docs/VOICE.md): clips from the voice manifest, Web Speech only for missing lines. */
  preRenderedVoice: boolean;
  /** WOW · feel: generative per-world background music (Settings → Music still turns it on). */
  worldMusic: boolean;
  /** WOW · feel: keep the music playing inside lessons (off: lessons stay quiet for the voice-over). */
  musicInLessons: boolean;
  /** WOW · parents: grown-ups corner behind the gate (weekly report, timelapse, family sharing). */
  parentArea: boolean;
  /** WOW · measure: upload the buffered funnel events to /api/v1/events (still needs analytics consent). */
  analyticsUpload: boolean;
  /** WOW · collect: creature book, sticker album and the framed gallery wall (rail entry + /collect). */
  enableCollections: boolean;
  /** WOW · habit: Today's drawing card, weekly rhythm garden, drawing calendar and kind reminder copy. */
  enableDailyHabit: boolean;
  /** WOW · living: finished paper drawings come alive (cut-out puppet) and are kept as creatures. */
  livingDrawings: boolean;
  /** WOW · first 2 minutes: right after the name, a very easy first drawing that comes alive + praise by name. */
  firstDrawingWow: boolean;
  /** WOW · first 2 minutes: with the first drawing on, skip the now redundant circle calibration + subjects preview. */
  compactOnboarding: boolean;

  /* ------------------------- Curriculum v2 (docs/CURRICULUM-V2.md) ------------------------- */
  /** Master switch: v2 paths (L1 5 / L2 7 / L3 8 / L4 9 activities per world). Off = the v1 path (48 lessons). */
  curriculumV2: boolean;
  /** v2 · level challenges (memory peek / no guides, "Confident artist" badge). */
  enableLevelChallenges: boolean;
  /** v2 · world stories: story chapters per level (storyBeat) + the finale on the map. */
  enableWorldStories: boolean;
  /** v2 · remix cards (part / param swaps of a core figure). */
  enableRemix: boolean;
  /** v2 · scenes (L2 / L3) composed from learnt figures. */
  enableScenes: boolean;
  /** v2 · world masterpiece (end of L4, resumable, golden frame). */
  enableMasterpiece: boolean;
  /** v2 · then & now (redraw the L1 subject with L4 skills). */
  enableThenNow: boolean;
  /** v2 · draw from real objects (observation side branch). */
  enableObserve: boolean;
  /** Draw from real objects · treasure hunt ("find something round at home…") + shape overlay. */
  objectHunt: boolean;
  /** Draw from real objects · the Object Museum (device-only gallery of object photos). */
  objectMuseum: boolean;
  /** v2 · crossovers (unlocked when both worlds are complete). */
  enableCrossovers: boolean;
  /** Picture tier ("Vẽ tranh"): a full-picture lesson per object lesson + exhibition room + Playground "Pictures". On in dev / demo. */
  pictureTier: boolean;
  /** Picture tier: world level the child must reach (key of level n-1) before a picture opens. */
  pictureMinLevel: number;
  /** v2 · scaffolding fade in the stroke player (trace ghost L1, guides L2, main shapes L3, none + hint L4). */
  enableScaffold: boolean;
  /** Draw-on-screen studio (docs/IDEAS-BACKLOG.md §A): brushes, fill, lesson palette by level, undo / redo, autosave per lesson. Off = the classic simple canvas. */
  enableDrawStudio: boolean;

  /* --------------------------- Monetization (docs/MONETIZATION-PLAN.md) --------------------------- */
  /**
   * Free boundary (remote-configurable, experiment T3): introFull = whole warm-up + first 2 activities
   * of each world; introL2 = warm-up L1-L2 + first 2 activities of each other world (launch default);
   * introL2PlusFavourite = introL2 + all of L1 of the favourite world.
   */
  freeBoundary: 'introFull' | 'introL2' | 'introL2PlusFavourite';
  /** A child tapping locked content sees a "wish" screen (no price) → wish jar / ask a parent. Off = gate → paywall. */
  wishJar: boolean;
  /** Where the onboarding paywall sits: right after the first living drawing comes alive (default), after the survey (v1) or none. */
  paywallPlacementOnboarding: 'afterFirstAlive' | 'afterSurvey' | 'none';
  /** Onboarding paywall hero: the child's own creature (default) or the generic illustration. */
  paywallHero: 'childCreature' | 'generic';
  /** Trial length (days) of the yearly offer; invited families get `referralTrialDays`. */
  trialDays: number;
  /** "Invite a family" (referral): invitee gets a longer trial, inviter +1 month when the invitee pays. */
  enableReferrals: boolean;
  referralTrialDays: number;
  /** Gifts bought on the web (Stripe; mock in dev), redeemed by a parent behind the gate. */
  enableGifts: boolean;
  /** Subscribers pick the book style (skins B / C are included in the subscription). */
  enableSkinSelector: boolean;
  /**
   * Selling switch per platform (docs/store/README.md). false = this platform sells nothing: no paywall, prices, plans,
   * restore, gifts, invites or wish → purchase path, and every profile gets the full content (as if on the Family plan)
   * so nothing looks locked without a way to unlock it. Android v1 ships with false until Play Billing (RevenueCat) lands;
   * flip it with CONFIG_OVERRIDES={"storeBilling":{"android":true}}.
   */
  storeBilling: Record<BillingPlatform, boolean>;
  /** Public share page (/s/:id) shows the child's first name. Off = a neutral "young artist" label (Families policy). */
  shareShowName: boolean;
  /**
   * Relatives may type a name + free-text compliment on the public share page. Off (default) = they pick who they are
   * and an award badge only: no unmoderated text reaches the child's journal.
   */
  shareCommentText: boolean;

  /* ------------------------- Story layer (docs/IDEAS-BACKLOG.md §E) ------------------------- */
  /** Story layer master switch: meet sheets + Character Cards (Residents tab), reactions to the child's drawing. */
  storyLayer: boolean;
  /** Story layer · step narration in the stroke player (character lines instead of the technical caption). */
  storyNarration: boolean;
  /** Story layer · chapter comics at each level start / end + the world finale spread. */
  storyComics: boolean;
  /** Story layer · "My story" storybook in the grown-ups corner: share + PDF export, bedtime mode. */
  storybookExport: boolean;
}

export type ArtStyleName = 'storybook' | 'sticker' | 'pastel';
export type BillingPlatform = 'ios' | 'android' | 'web';
export const ART_STYLE_NAMES: ArtStyleName[] = ['storybook', 'sticker', 'pastel'];
/** Live-config skin name → art style id used by the renderer. */
export const artStyleId = (n: ArtStyleName | undefined): 'A' | 'B' | 'C' => (n === 'sticker' ? 'B' : n === 'pastel' ? 'C' : 'A');

export const DEFAULT_CONFIG: LiveConfig = {
  enableStudio: true,
  enableStories: true,
  enableWeeklyChallenges: true,
  enableTeacherMailing: true,
  enableDrawTogether: true,
  enableHomeWidgetActivation: false,
  skillJourneysEnabled: true,
  isSkillProgressionEnabled: true,
  requestReviewAppEnabled: true,
  enableSuggestions: true,
  enableTipsAndTricksAnimatedThumbnails: true,
  enableLooseTutorial: true,
  allowSkipCapture: true,
  skipScanImageForJournal: false,
  skipShareBeforeAward: false,
  journalMode: 'book',
  familyPlanPaywallVariant: 'familyFirst',
  singleFtuePaywall: true,
  onboarding: {
    grownUpGate: true,
    selfAttributionQuestion: true,
    favoriteSubjects: 'selection',
    realDrawer: true,
    circleScan: true,
    socialProof: true,
    reminderNudge: true,
    kidLedSecondProfile: true,
    ftueUnlockAll: true,
  },
  appUpdate: { minimalBuild: 0, optionalBuild: 0 },
  recognitionEnabled: true,
  blockManagedDevices: true,
  enableRemotePush: true,
  nativeReviewEnabled: true,
  artStyle: 'storybook',
  enableColourWorld: true,
  enableColourCoach: true,
  junoReactions: true,
  preRenderedVoice: true,
  worldMusic: true,
  musicInLessons: false,
  parentArea: true,
  analyticsUpload: true,
  enableCollections: true,
  enableDailyHabit: true,
  livingDrawings: true,
  firstDrawingWow: true,
  compactOnboarding: true,
  curriculumV2: true,
  enableLevelChallenges: true,
  enableWorldStories: true,
  enableRemix: true,
  enableScenes: true,
  enableMasterpiece: true,
  enableThenNow: true,
  enableObserve: true,
  objectHunt: true,
  objectMuseum: true,
  enableCrossovers: true,
  pictureTier: true,
  pictureMinLevel: 2,
  enableScaffold: true,
  enableDrawStudio: true,
  freeBoundary: 'introL2',
  wishJar: true,
  paywallPlacementOnboarding: 'afterFirstAlive',
  paywallHero: 'childCreature',
  trialDays: 7,
  enableReferrals: true,
  referralTrialDays: 14,
  enableGifts: true,
  enableSkinSelector: true,
  storeBilling: { ios: true, android: false, web: true },
  shareShowName: false,
  shareCommentText: false,
  storyLayer: true,
  storyNarration: true,
  storyComics: true,
  storybookExport: true,
};

export interface LimitTo {
  ageRange?: [number, number];
  countryCodes?: string[];
  languages?: string[];
  deviceTypes?: ('phone' | 'tablet' | 'desktop')[];
}

export interface Experiment {
  id: string;
  /** Bucket name → weight (percent). */
  buckets: Record<string, number>;
  limitTo?: LimitTo;
  /** Overrides applied for each bucket (deep-merged into the config). */
  values: Record<string, DeepPartial<LiveConfig>>;
}

export type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

export interface Attributes {
  accountId: string;
  age?: number | null;
  country?: string;
  language?: string;
  deviceType?: 'phone' | 'tablet' | 'desktop';
}

export function matchesLimit(limit: LimitTo | undefined, a: Attributes): boolean {
  if (!limit) return true;
  if (limit.ageRange && (a.age == null || a.age < limit.ageRange[0] || a.age > limit.ageRange[1])) return false;
  if (limit.countryCodes && (!a.country || !limit.countryCodes.includes(a.country.toUpperCase()))) return false;
  if (limit.languages && (!a.language || !limit.languages.includes(a.language.slice(0, 2)))) return false;
  if (limit.deviceTypes && (!a.deviceType || !limit.deviceTypes.includes(a.deviceType))) return false;
  return true;
}

/** Deterministic bucket for (experiment, account): stable across devices and sessions. */
export function assignBucket(exp: Experiment, accountId: string): string {
  const entries = Object.entries(exp.buckets);
  const total = entries.reduce((n, [, w]) => n + w, 0) || 1;
  let roll = hash(`${exp.id}:${accountId}`) % total;
  for (const [name, w] of entries) {
    if (roll < w) return name;
    roll -= w;
  }
  return entries[0]?.[0] ?? 'control';
}

function deepMerge<T>(base: T, over: DeepPartial<T> | undefined): T {
  if (!over) return base;
  const out = { ...base } as Record<string, unknown>;
  for (const [k, v] of Object.entries(over)) {
    const cur = out[k];
    out[k] = v && typeof v === 'object' && !Array.isArray(v) && cur && typeof cur === 'object' ? deepMerge(cur, v as object) : v;
  }
  return out as T;
}

export interface ResolvedConfig {
  config: LiveConfig;
  buckets: Record<string, string>;
}

/**
 * Resolve the config for one user: defaults ← server overrides ← experiment buckets ← debug
 * bucket overrides (cheats menu).
 */
export function resolveConfig(
  experiments: Experiment[],
  attrs: Attributes,
  opts: { overrides?: DeepPartial<LiveConfig>; debugBuckets?: Record<string, string> } = {},
): ResolvedConfig {
  let config = deepMerge(DEFAULT_CONFIG, opts.overrides);
  const buckets: Record<string, string> = {};
  for (const exp of experiments) {
    if (!matchesLimit(exp.limitTo, attrs)) continue;
    const b = opts.debugBuckets?.[exp.id] ?? assignBucket(exp, attrs.accountId);
    buckets[exp.id] = b;
    config = deepMerge(config, exp.values[b]);
  }
  return { config, buckets };
}

/** Our own experiments (examples of the mechanism). */
export const EXPERIMENTS: Experiment[] = [
  {
    id: 'paywall-layout',
    buckets: { familyFirst: 50, cadenceFirst: 50 },
    values: { familyFirst: { familyPlanPaywallVariant: 'familyFirst' }, cadenceFirst: { familyPlanPaywallVariant: 'cadenceFirst' } },
  },
  {
    id: 'favorite-subjects-style',
    buckets: { selection: 50, animated: 50 },
    limitTo: { ageRange: [4, 12] },
    values: { selection: { onboarding: { favoriteSubjects: 'selection' } }, animated: { onboarding: { favoriteSubjects: 'animated' } } },
  },
  {
    // Journal presentation (journalMode). Everyone gets the book until the grid bucket is given weight;
    // QA can force either bucket from the cheats menu (debug buckets).
    id: 'journal-mode',
    buckets: { book: 100, grid: 0 },
    values: { book: { journalMode: 'book' }, grid: { journalMode: 'grid' } },
  },
];

/** Can this platform sell (paywall, prices, restore, gifts, invites)? Missing entries count as on. */
export function storeBillingOn(cfg: Pick<LiveConfig, 'storeBilling'> | undefined, platform: BillingPlatform): boolean {
  return cfg?.storeBilling?.[platform] !== false;
}

/**
 * The config a client on `platform` runs with: where selling is off, the money surfaces are switched off too
 * (onboarding paywall, invites, gifts, skin wishes are moot because every profile has full access).
 */
export function platformConfig(cfg: LiveConfig, platform: BillingPlatform): LiveConfig {
  if (storeBillingOn(cfg, platform)) return cfg;
  return { ...cfg, paywallPlacementOnboarding: 'none', enableReferrals: false, enableGifts: false };
}

/** App update gate. */
export function appUpdateRequirement(build: number, cfg: LiveConfig['appUpdate']): 'none' | 'optional' | 'force' {
  if (build < cfg.minimalBuild) return 'force';
  if (build < cfg.optionalBuild) return 'optional';
  return 'none';
}
